// ── Cuándo sale un recorrido, y cuándo no ────────────────────────────────────
//
// Tres condiciones, y las tres tienen que dar que sí:
//
//   1. El módulo tiene recorrido escrito EN ESTE IDIOMA.
//   2. Esta persona no lo vio todavía  → `user_ui_prefs`, o sea el servidor:
//      viaja entre máquinas, igual que el estado de las columnas.
//   3. No salió ya otro en esta visita → `sessionStorage`.
//
// La tercera es de Eduardo (2026-09-11): *"si entro a seis módulos el primer
// día, quiero aprender uno y que los otros cinco esperen a mañana. Seis
// seguidos no se aprenden, se saltean"*.
//
// Y por eso el tope vive en la SESIÓN del navegador y no en el servidor: la
// idea es "uno por visita", así que volver mañana tiene que volver a ofrecer
// uno. Los que no salieron NO se marcan como vistos — esperan su turno.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cargarRecorrido, type ModuloConRecorrido, type PasoDeRecorrido } from '@evo-hub/help-content/recorridos';
import type { PasoVisible } from '../components/ui/Recorrido.js';
import { useUiFlag } from './useUiPrefs.js';
import { useToast } from '../components/ui/Toast.js';

const CLAVE_SESION = 'evo-recorrido-visto-en-esta-sesion';

function yaSalioUnoEnEstaSesion(): boolean {
  try { return sessionStorage.getItem(CLAVE_SESION) === 'true'; } catch { return false; }
}

function marcarQueSalioUno(): void {
  try { sessionStorage.setItem(CLAVE_SESION, 'true'); } catch { /* sin almacenamiento */ }
}

/**
 * De los pasos escritos, los que tienen sentido en esta pantalla ahora mismo.
 *
 * - `anchoMin`: el menú lateral no existe en 390px; señalarlo ahí es señalar
 *   el aire.
 * - `cuando`: con el módulo vacío el recorrido corre igual, pero mostrando
 *   dónde se crea la primera cosa (decisión de Eduardo). Un paso puede existir
 *   solo para el caso vacío y otro solo para el caso con datos, en vez de un
 *   texto tibio que sirva para los dos.
 */
export function pasosVisibles(
  pasos: PasoDeRecorrido[],
  opts: { ancho: number; hayDatos: boolean },
): PasoVisible[] {
  return pasos
    .filter(p => (p.anchoMin === undefined || opts.ancho >= p.anchoMin))
    .filter(p => (p.cuando === undefined
      || (p.cuando === 'vacio' && !opts.hayDatos)
      || (p.cuando === 'con-datos' && opts.hayDatos)))
    .map(p => ({ ancla: p.ancla, titulo: p.titulo, texto: p.texto }));
}

interface Opciones {
  /** Si el módulo tiene algo cargado. Cambia qué pasos salen. */
  hayDatos: boolean;
  /** Para no abrirlo encima de una pantalla que todavía está cargando: un
   *  recorte sobre un esqueleto mide el esqueleto. */
  listo?: boolean;
}

export function useRecorrido(modulo: ModuloConRecorrido, { hayDatos, listo = true }: Opciones) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const idioma = i18n.language.startsWith('en') ? 'en' : 'es';
  const [visto, setVisto] = useUiFlag(`recorrido.${modulo}`, false);
  const [abierto, setAbierto] = useState(false);

  const pasos = useMemo(
    () => pasosVisibles(cargarRecorrido(modulo, idioma), {
      ancho: typeof window === 'undefined' ? 1440 : window.innerWidth,
      hayDatos,
    }),
    [modulo, idioma, hayDatos],
  );

  // Pedido explícito: `?recorrido=1` en la URL. Lo usa el paso de la inducción
  // para traer a alguien hasta acá y que el recorrido salga aunque ya lo haya
  // visto. Ignora las tres condiciones a propósito: pedirlo es una decisión,
  // y el tope de uno por sesión existe para no avasallar a quien NO lo pidió.
  //
  // Lo que NO puede pasar, y pasó (2026-09-11): que alguien lo pida y no ocurra
  // NADA. Sin pasos —porque el idioma no tiene recorrido escrito, o porque
  // todos se filtraron— la salida es decirlo, no encogerse de hombros. Un
  // recorrido roto se salta en silencio para no romperle la pantalla a nadie;
  // un pedido de una persona merece una respuesta.
  useEffect(() => {
    // Sin `listo` no se hace nada TODAVÍA: el pedido sigue en la URL y este
    // efecto vuelve a correr cuando la pantalla termine de cargar. Lo que no
    // puede pasar es que `listo` no llegue nunca — por eso significa "terminó
    // de cargar" y no "hay datos", que es el error que tenía FunnelsPage.
    if (!listo) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('recorrido') !== '1') return;
    // Se saca de la URL para que recargar no lo vuelva a disparar, y para que
    // el enlace copiado no lleve el recorrido pegado.
    url.searchParams.delete('recorrido');
    window.history.replaceState({}, '', url.toString());
    if (pasos.length === 0) { toast.info(t('recorrido.sinPasos')); return; }
    setAbierto(true);
  }, [listo, pasos.length, toast, t]);

  // La primera visita. Se decide una sola vez, cuando la pantalla ya está
  // dibujada: si se decidiera mientras carga, `hayDatos` sería false para todos
  // y siempre saldría el recorrido del módulo vacío.
  useEffect(() => {
    if (!listo || visto || abierto || pasos.length === 0) return;
    if (yaSalioUnoEnEstaSesion()) return;
    marcarQueSalioUno();
    setAbierto(true);
  }, [listo, visto, abierto, pasos.length]);

  const cerrar = useCallback(() => {
    // Salir cuenta como visto: nadie quiere que le vuelva a salir mañana lo
    // mismo que ya cerró. Sigue estando a pedido desde la ayuda.
    setAbierto(false);
    setVisto(true);
  }, [setVisto]);

  const terminar = useCallback(() => {
    setAbierto(false);
    setVisto(true);
  }, [setVisto]);

  /** Volver a verlo a pedido — desde la ayuda del módulo. Ignora las tres
   *  condiciones: pedirlo es una decisión, no una sugerencia del sistema. */
  const abrirAPedido = useCallback(() => { setAbierto(true); }, []);

  return { pasos, abierto, cerrar, terminar, abrirAPedido, hayRecorrido: pasos.length > 0 };
}
